import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  adminGetGallery, adminDeleteGallery,
  adminGetVideos, adminDeleteVideo,
  adminGetEvents, adminUpdateEventStatus, adminDeleteEvent,
  adminGetPackages,
  adminGetFaqs,
} from '@/lib/api';
import { Image, Video, CalendarDays, Plus, LogOut, X, RefreshCw, Film, PartyPopper, HelpCircle } from 'lucide-react';

import { HeroSettingsPanel } from './admin/HeroSettingsPanel';
import { PackagesPanel } from './admin/PackagesPanel';
import { FaqPanel } from './admin/FaqPanel';
import { AdminForm, GalleryGrid, VideoList, EventList } from './admin/ContentPanels';

const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN = 403;

const ADD_LABELS = { gallery: 'Photo', videos: 'Video', events: 'Event', packages: 'Package' };

export default function AdminDashboard() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('gallery');
  const [gallery, setGallery] = useState([]);
  const [videos, setVideos] = useState([]);
  const [events, setEvents] = useState([]);
  const [packages, setPackages] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [g, v, e, p, f] = await Promise.all([adminGetGallery(), adminGetVideos(), adminGetEvents(), adminGetPackages(), adminGetFaqs()]);
      setGallery(g);
      setVideos(v);
      setEvents(e);
      setPackages(p);
      setFaqs(f);
    } catch (err) {
      if (err.response?.status === HTTP_UNAUTHORIZED || err.response?.status === HTTP_FORBIDDEN) navigate('/login');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!isAdmin) { navigate('/login'); return; }
    loadData();
  }, [isAdmin, navigate, loadData]);

  const handleLogout = async () => { await logout(); navigate('/'); };

  if (!isAdmin) return null;

  const tabs = [
    { id: 'hero', label: 'Hero Video', icon: Film, count: null },
    { id: 'packages', label: 'Packages', icon: PartyPopper, count: packages.length },
    { id: 'gallery', label: 'Gallery', icon: Image, count: gallery.length },
    { id: 'videos', label: 'Videos', icon: Video, count: videos.length },
    { id: 'events', label: 'Events', icon: CalendarDays, count: events.length },
    { id: 'faq', label: 'FAQ', icon: HelpCircle, count: faqs.length },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#050D1A] pt-20">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
            <p className="text-slate-400 text-sm">Welcome, {user?.name || user?.email}</p>
          </div>
          <button onClick={handleLogout} data-testid="admin-logout" className="flex items-center gap-2 text-slate-400 hover:text-red-400 text-sm transition-colors">
            <LogOut size={16} /> Logout
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-8">
          {tabs.map(t => (
            <button key={t.id} onClick={() => { setTab(t.id); setShowForm(false); }} data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                tab === t.id ? 'bg-[#F26522] text-white' : 'bg-white/5 text-slate-400 hover:text-white'
              }`}>
              <t.icon size={16} /> {t.label} {t.count !== null && `(${t.count})`}
            </button>
          ))}
          <button onClick={loadData} className="ml-auto p-2.5 rounded-xl bg-white/5 text-slate-400 hover:text-white transition-colors" data-testid="admin-refresh">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>

        {tab === 'hero' && <HeroSettingsPanel />}

        {tab === 'packages' && <PackagesPanel packages={packages} onRefresh={loadData} />}

        {tab === 'faq' && <FaqPanel faqs={faqs} onRefresh={loadData} />}

        {tab !== 'hero' && tab !== 'packages' && tab !== 'faq' && (
          <>
            <button onClick={() => setShowForm(!showForm)} data-testid="admin-add-btn"
              className="mb-6 flex items-center gap-2 bg-[#F26522] hover:bg-[#D9551A] text-white px-5 py-2.5 rounded-xl font-medium text-sm transition-all">
              {showForm ? <X size={16} /> : <Plus size={16} />}
              {showForm ? 'Cancel' : `Add ${ADD_LABELS[tab] || ''}`}
            </button>
            {showForm && <AdminForm tab={tab} onSuccess={() => { setShowForm(false); loadData(); }} />}
            {tab === 'gallery' && <GalleryGrid items={gallery} onDelete={async (id) => { await adminDeleteGallery(id); loadData(); }} />}
            {tab === 'videos' && <VideoList items={videos} onDelete={async (id) => { await adminDeleteVideo(id); loadData(); }} />}
            {tab === 'events' && <EventList items={events} onDelete={async (id) => { await adminDeleteEvent(id); loadData(); }} onStatusChange={async (id, s) => { await adminUpdateEventStatus(id, s); loadData(); }} />}
          </>
        )}
      </div>
    </div>
  );
}
